import {
  PURCHASE_MSG,
  RECEIVED_ACCEPT,
  RECEIVED_BATCH_MAX,
  RECEIVED_KINDS,
  RECEIVED_KIND_LABEL,
  RECEIVED_MAX_BYTES,
  RECEIVED_MSG,
  RECEIVED_PHONE_ACCEPT,
  RECEIVED_READ_MSG,
  RECEIVED_TEXT_MAX,
  RECEIVED_TEXT_MIN,
  RECEIVED_TITLE_MAX,
  RECEIVED_UPLOAD_TIMEOUT_MS,
  encodeTitleHeader,
  isReceivedFile,
  pasteKeyOf,
  pastedImageTitle,
  receivedKindOption,
  receivedTypeOf,
  type GroupDetail,
  type ReceivedDocument,
  type ReceivedKind,
  type ReceivedList,
} from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, Link } from "react-router";
import { api, apiUpload, messageOf } from "../../api.js";
import { useMe } from "../../auth.js";
import { FileDrop, type FileDropSource } from "../../components/FileDrop.jsx";
import { Trunc } from "../../components/GroupList.jsx";
import { CheckIcon, DoneIcon, KindIcon, QueuedIcon, ScanIcon, WarnIcon } from "../../components/icons.jsx";
import { RowMenu } from "../../components/RowMenu.jsx";
import { Sheet } from "../../components/Sheet.jsx";
import { ErrorText, Field, Help, guardComposingEnter, useToast } from "../../components/ui.jsx";
import { useModule } from "../../core/slots.jsx";
import { uses } from "../../core/uses.js";
import { t } from "../../labels.js";
import { fmtDateTimeShort } from "../../lib/dates.js";
import { useDropOverlay } from "../../lib/dropOverlay.js";
import { useNarrow } from "../../lib/useNarrow.js";
import { currentView } from "../../lib/view.js";
import { ReceivedReadDrawer } from "./ReceivedReadDrawer.jsx";
import { ReceivedViewer } from "./ReceivedViewer.jsx";

type Source = FileDropSource | "paste";
/** 送り待ち・送っている・送れなかった行（一覧の上に出す）。retry＝「もう一度」を出す（送る前に分かる間違い＝形式・大きさ・件数は出さない） */
type Pending = { key: string; name: string; state: "waiting" | "adding" | "error"; msg?: string | undefined; retry: boolean; run: () => Promise<unknown> };
/** 2分待っても返事が来なかった（打ち切った） */
class UploadTimeout extends Error {}

const keyOf = (groupId: string) => ["received", groupId] as const;
/** 案件の受け取った書類の一覧（案件の画面が先に読む口 registerSlotPreload と、枠の中の問い合わせが同じものを使う。#179） */
export const receivedListQuery = (groupId: string) => ({ queryKey: keyOf(groupId), queryFn: () => api<ReceivedList>("GET", `/groups/${groupId}/received-documents`) });
const pasteKey = (): string => pasteKeyOf(typeof navigator !== "undefined" ? navigator.platform || navigator.userAgent : "");
/**
 * 一度に送るのは1つ（S2、現場力 #162）。2つ同時だと、射場さんの Mac の Safari で複数を落としたとき要求が API に届かないまま2つとも返らず、
 * 順番待ちが埋まって次の1枚も入らなかった。1件を待つのは2分まで（RECEIVED_UPLOAD_TIMEOUT_MS）＝打ち切って「送れませんでした」にして次へ
 */
const PARALLEL = 1;

/**
 * 案件の画面の枠「受け取った書類」（部品 received、../eigyouryoku/docs/13 §4.1・§4.2。差し込み口 group.received、門は core）。
 *   ・使う機能 receivedDocs の会社だけ（OFF は枠ごと隠す＝データは残る）。案件詳細と社内メモのあいだ
 *   ・0件＝点線の領域だけの低い枠。1件以上＝点線（低く）＋表（種類・名前・P.n・登録日時・登録者・状態・「…」）＋「削除済み n ›」
 *     登録日時は created_at を「10/11 19:40」（違う年は「2025/10/11 19:40」。S3＝lib/dates.ts の fmtDateTimeShort）
 *     スマホ（幅 767px 以下とスマホ版＝small。S4＝docs/13 §4.6 ⑦、絵＝received-read/Phone・PhoneSheet）は1件を2段のグリッド（高さ 72px。.rdtable.is-small）：
 *       1段目＝名前（太字・「…」・押すと引き出し）と P.n、右の 44px は ⋮（2段分）／2段目＝書類のチップ（32px・押すと下からシートで6つから選ぶ。未選択は淡い地の「書類選択」）・
 *       登録日時・登録者は姓だけ（折り返さない）／状態があるときだけ3段目
 *   ・入れ方：枠に落とす／「ファイル選択」／画面のどこに落としても（重ね「受け取った書類に追加」）／⌘V（画像＝写真、文字＝シートで確かめて資料）
 *   ・送るのは1つずつ順に（S2）。待っている行は「順番待ち」、送っている行は「追加中…」、送れなかった行（2分で打ち切り＝「送れませんでした」も）は朱の文と「もう一度」「取消」。
 *     一度に落とす・選ぶのは10件まで（11件目からは送らず「一度に入れられるのは10件までです」）
 *   ・書類の既定は API が決める（写真＝写真、PDF・TIFF・文字＝未選択「—」。文字の層のある PDF は言葉で）。未選択の行は書類の欄の地を淡く、題の右に「書類が未選択 n件」（スマホは「未選択 n件」）
 *   ・読み取り（2回目、R2。S3＝R3 で「AI」を名乗る・絵は screen_sample/design_2026-10-11/received-read/Main）：オプション doc_read の会社だけ「状態」の列に中身（OFF の会社は1回目のまま空）。
 *     注文書・見積依頼 → 「AI読み取り」（淡い藍・36px・走査線のアイコン。乗せると藍）／
 *     「AI読み取り中」（同じ形のまま走査線だけが上下に動く＋薄墨の「約30秒」。queued は点線の丸＋「順番待ち」）／
 *     「読み取り済み ›」（押すと確認の引き出し）／「✓ 取込済 → 下書き ›」（押すと見積）／読めなかった → 朱の「読み取れませんでした」＋「もう一度」（同じ形のボタン）。
 *     差出人が{customer}と異なる読み取り済み（API の readWarnings に sender、S4 ①）は朱の「⚠ 読み取り済み ›」＋下に 13px の「差出人が{customer}と異なります」（title にも）。
 *     月の上限なら「AI読み取り」は押せない（地 --head・薄墨）＋横に「今月の上限 n通」。未選択の行は薄墨で「書類選択」。
 *     読み取り中の行があるあいだ一覧を5秒ごとに読み直す。題の右に「今月のAI読み取り n／m通」（上限なし＝受付開始前は出さない）
 *   ・スマホ（small）は AI読み取りのボタンと引き出しを出さない（S4 ⑥。確認の画面は狭くて使えない）：状態は文字だけ＝「読み取り済み（確認は PC で）」・
 *     朱の ⚠「読み取り済み・差出人が{customer}と異なります（確認は PC で）」・「AI読み取り中」・「読み取れませんでした」・「取込済 → 下書き ›」（押せる）。今月の通数も出さない
 */
export function ReceivedBox({ group }: { group: GroupDetail }) {
  const me = useMe();
  const on = uses(me.data, "receivedDocs");
  if (!on) return null;
  return <ReceivedBoxInner group={group} />;
}

function ReceivedBoxInner({ group }: { group: GroupDetail }) {
  const gid = group.id;
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const phone = currentView() === "phone";
  const narrow = useNarrow();
  /** スマホの形（幅 767px 以下かスマホ版）。読み取りのボタンと引き出しを出さない・一覧は2段（S4） */
  const small = phone || narrow;
  const [showDeleted, setShowDeleted] = useState(false);
  const docRead = useModule("doc_read");
  /** 請求書を仕入にする（#233 3a）のは sales の会社だけ＝請求書の「AI読み取り」も sales の会社だけ */
  const salesOn = useModule("sales");
  const nav = useNavigate();
  // 読み取り中の行があるあいだ 5秒ごとに読み直す（{vendorQuote}の一覧と同じ。この GET は利用状況の種類に無い＝数えない）
  const list = useQuery({
    ...receivedListQuery(gid),
    refetchInterval: (q) => (docRead && q.state.data?.items.some((d) => d.readStatus === "queued" || d.readStatus === "running") ? 5000 : false),
  });
  const deleted = useQuery({ queryKey: [...keyOf(gid), "deleted"], queryFn: () => api<ReceivedList>("GET", `/groups/${gid}/received-documents?deleted=1`), enabled: showDeleted });
  const refresh = useCallback(() => void qc.invalidateQueries({ queryKey: keyOf(gid) }), [qc, gid]);

  // ---- 送る（1つずつ順に・1件2分まで） ----
  const [pending, setPending] = useState<Pending[]>([]);
  const seq = useRef(0);
  const enqueue = (name: string, run: () => Promise<unknown>, error?: string) => {
    seq.current += 1;
    const key = `p${seq.current}`;
    setPending((p) => [...p, error ? { key, name, state: "error", msg: error, retry: false, run } : { key, name, state: "waiting", retry: true, run }]);
  };
  useEffect(() => {
    const running = pending.filter((p) => p.state === "adding").length;
    const next = pending.filter((p) => p.state === "waiting").slice(0, Math.max(0, PARALLEL - running));
    if (!next.length) return;
    const keys = new Set(next.map((n) => n.key));
    setPending((p) => p.map((x) => (keys.has(x.key) ? { ...x, state: "adding" } : x)));
    for (const n of next) {
      n.run().then(
        () => {
          setPending((p) => p.filter((x) => x.key !== n.key));
          refresh();
        },
        (e: unknown) => setPending((p) => p.map((x) => (x.key === n.key ? { ...x, state: "error", msg: e instanceof UploadTimeout ? RECEIVED_MSG.timeout : messageOf(e) } : x))),
      );
    }
  }, [pending, refresh]);

  const addFiles = (files: File[], source: Source) => {
    files.forEach((f, i) => {
      const title = (source === "paste" && f.type.startsWith("image/") ? pastedImageTitle(new Date()) : f.name || pastedImageTitle(new Date())).slice(0, RECEIVED_TITLE_MAX);
      // 1件を2分まで待つ（返事が来なければ打ち切って次へ）
      const run = async () => {
        const ac = new AbortController();
        const timer = setTimeout(() => ac.abort(), RECEIVED_UPLOAD_TIMEOUT_MS);
        try {
          return await apiUpload<ReceivedDocument>(`/groups/${gid}/received-documents`, f, receivedTypeOf(f), { "x-gb-title": encodeTitleHeader(title), "x-gb-source": source }, ac.signal);
        } catch (e) {
          throw ac.signal.aborted ? new UploadTimeout() : e;
        } finally {
          clearTimeout(timer);
        }
      };
      // 送る前に分かる間違いは送らずに行で知らせる（件数・形式・大きさ。ページ数・1案件の件数・容量は API の答え）
      if (i >= RECEIVED_BATCH_MAX) enqueue(title, run, RECEIVED_MSG.tooMany);
      else if (!isReceivedFile(f)) enqueue(title, run, RECEIVED_MSG.type);
      else if (f.size > RECEIVED_MAX_BYTES) enqueue(title, run, RECEIVED_MSG.tooBig);
      else enqueue(title, run);
    });
  };

  // ---- 貼り付けた文字（シートで確かめてから） ----
  const [pasteText, setPasteText] = useState<string | null>(null);
  const [pasteErr, setPasteErr] = useState<string | undefined>();
  const [pasteBusy, setPasteBusy] = useState(false);
  const addText = async () => {
    if (pasteText == null) return;
    setPasteBusy(true);
    setPasteErr(undefined);
    try {
      await api<ReceivedDocument>("POST", `/groups/${gid}/received-documents/text`, { body: pasteText });
      setPasteText(null);
      refresh();
    } catch (e) {
      setPasteErr(messageOf(e));
    } finally {
      setPasteBusy(false);
    }
  };

  // ---- 画面全体の重ねと貼り付け ----
  const over = useDropOverlay({
    enabled: !phone,
    onFiles: (files) => addFiles(files, "drop"),
    onPasteFiles: (files) => addFiles(files, "paste"),
    onPasteText: (text) => {
      if (text.trim().length < RECEIVED_TEXT_MIN) return; // 短い文は何もしない
      setPasteErr(text.length > RECEIVED_TEXT_MAX ? RECEIVED_MSG.textTooLong : undefined);
      setPasteText(text);
    },
  });

  // ---- 引き出し・名前変更・削除 ----
  const [viewing, setViewing] = useState<ReceivedDocument | null>(null);
  const [reading, setReading] = useState<ReceivedDocument | null>(null);
  const closeReading = useCallback(() => setReading(null), []);
  const startRead = async (d: ReceivedDocument) => {
    try {
      await api<ReceivedDocument>("POST", `/received-documents/${d.id}/read`);
      refresh();
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  /** 今月の通数（上限なし＝受付開始前は null）。上限に達していれば「AI読み取り」は押せない（やり直しの「もう一度」は API が今月すでに数えた書類を止めないので押せる） */
  const quota = list.data?.quota ?? null;
  const quotaFull = quota?.limit != null && quota.used >= quota.limit;
  /** 状態の列（doc_read の会社だけ）。注文書・見積依頼でなければ空（未選択は「種類を選ぶと読み取れます」、取込済は種類を変えても出す） */
  const stateCell = (d: ReceivedDocument) => {
    if (!docRead) return null;
    if (small && !d.imported) return phoneStateCell(d);
    if (d.imported) {
      return (
        <span className="rdst rdst-done">
          <DoneIcon />
          <button type="button" className="rdst-link" onClick={() => nav(d.imported!.target === "purchase" ? `/purchases/${d.imported!.documentId}` : `/estimates/${d.imported!.documentId}`)} data-testid="received-imported">
            {RECEIVED_READ_MSG.imported} → {d.imported.target === "purchase" ? PURCHASE_MSG.menu : (d.imported.documentNo ?? RECEIVED_READ_MSG.importedDraft)} ›
          </button>
        </span>
      );
    }
    if (d.kind === "unsorted") {
      return (
        <span className="rdst rdst-hint" data-testid="received-pick-kind">
          {RECEIVED_READ_MSG.pickKindHint}
        </span>
      );
    }
    if (d.kind !== "order" && d.kind !== "inquiry" && !(d.kind === "invoice" && salesOn)) return null;
    if (d.readStatus === "queued") {
      return (
        <span className="rdst rdst-wait" role="status" data-testid="received-reading" data-state="queued">
          <QueuedIcon />
          {RECEIVED_READ_MSG.queuedLabel}
        </span>
      );
    }
    if (d.readStatus === "running") {
      // 同じ形（淡い藍の地）のまま走査線だけが動く。動きを減らす設定では止まり、文字はそのまま（色・動きだけで伝えない）
      return (
        <span className="rdbtn is-busy" role="status" data-testid="received-reading" data-state="running">
          <ScanIcon moving testId="rd-scan" />
          <span className="rd-t">{RECEIVED_READ_MSG.reading}</span>
          <span className="rd-eta">{RECEIVED_READ_MSG.etaHint}</span>
        </span>
      );
    }
    if (d.readStatus === "done") {
      if (d.readWarnings.includes("sender")) {
        const why = t(RECEIVED_READ_MSG.warnSender);
        return (
          <span className="rdst-warn">
            <button type="button" className="rdst-link is-warn" title={why} onClick={() => setReading(d)} data-testid="received-read-done" data-warn="sender">
              <WarnIcon />
              {RECEIVED_READ_MSG.done}
            </button>
            <span className="rdst-why" title={why} data-testid="received-read-warn">
              {why}
            </span>
          </span>
        );
      }
      return (
        <button type="button" className="rdst-link" onClick={() => setReading(d)} data-testid="received-read-done">
          {RECEIVED_READ_MSG.done}
        </button>
      );
    }
    if (d.readStatus === "failed") {
      return (
        <span className="rdst rdst-fail">
          <span className="shu" title={d.readError ?? undefined} data-testid="received-read-failed">
            {RECEIVED_READ_MSG.failed}
          </span>
          <button type="button" className="rdbtn" onClick={() => void startRead(d)} data-testid="received-read-again">
            <ScanIcon />
            {RECEIVED_READ_MSG.again}
          </button>
        </span>
      );
    }
    return (
      <span className="rdst rdst-read">
        <button type="button" className="rdbtn" disabled={quotaFull} onClick={() => void startRead(d)} data-testid="received-read">
          <ScanIcon />
          {RECEIVED_READ_MSG.read}
        </button>
        {quotaFull && quota?.limit != null ? <span className="rd-eta" data-testid="received-quota-full">{RECEIVED_READ_MSG.quotaFull(quota.limit)}</span> : null}
      </span>
    );
  };
  /** スマホの状態（文字だけ。取込済は PC と同じ「取込済 → 下書き ›」） */
  const phoneStateCell = (d: ReceivedDocument) => {
    if (d.kind !== "order" && d.kind !== "inquiry" && !(d.kind === "invoice" && salesOn)) return null;
    if (d.readStatus === "queued" || d.readStatus === "running") {
      return (
        <span className="rdst rdst-text" role="status" data-testid="received-reading" data-state={d.readStatus}>
          {RECEIVED_READ_MSG.reading}
        </span>
      );
    }
    if (d.readStatus === "done") {
      return d.readWarnings.includes("sender") ? (
        <span className="rdst rdst-text is-warn" data-testid="received-read-phone" data-warn="sender">
          <WarnIcon size={16} />
          <span>{t(RECEIVED_READ_MSG.phoneDoneWarn)}</span>
        </span>
      ) : (
        <span className="rdst rdst-text" data-testid="received-read-phone">
          {RECEIVED_READ_MSG.phoneDone}
        </span>
      );
    }
    if (d.readStatus === "failed") {
      return (
        <span className="rdst rdst-text shu" title={d.readError ?? undefined} data-testid="received-read-failed">
          {RECEIVED_READ_MSG.failed}
        </span>
      );
    }
    return null;
  };
  /** スマホの書類のチップ → 下からシート（S4 ⑦）。選ぶと PATCH → 閉じる → 一覧を読み直す */
  const [kindSheet, setKindSheet] = useState<{ doc: ReceivedDocument; open: boolean } | null>(null);
  const [renaming, setRenaming] = useState<ReceivedDocument | null>(null);
  const [deleting, setDeleting] = useState<ReceivedDocument | null>(null);
  const [purging, setPurging] = useState<ReceivedDocument | null>(null);
  const [busy, setBusy] = useState(false);
  const closeViewer = useCallback(() => setViewing(null), []);
  const changeKind = async (d: ReceivedDocument, kind: ReceivedKind) => {
    try {
      await api<ReceivedDocument>("PATCH", `/received-documents/${d.id}`, { kind });
      refresh();
      toast(RECEIVED_MSG.kindToast.replace("{kind}", RECEIVED_KIND_LABEL[kind]));
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await api<ReceivedDocument>("DELETE", `/received-documents/${deleting.id}`);
      if (viewing?.id === deleting.id) setViewing(null);
      setDeleting(null);
      refresh();
      toast(RECEIVED_MSG.deletedToast);
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(false);
    }
  };
  const restore = async (d: ReceivedDocument) => {
    try {
      await api<ReceivedDocument>("POST", `/received-documents/${d.id}/restore`);
      refresh();
      toast(RECEIVED_MSG.restoredToast);
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  // 完全に削除（#281）：削除済の行だけ。原本も消え元に戻せない＝窓で確かめる。送付済の見積依頼の添付などは API の 409 の文を通知
  const purge = async () => {
    if (!purging) return;
    setBusy(true);
    try {
      await api<void>("DELETE", `/received-documents/${purging.id}/purge`);
      if (viewing?.id === purging.id) setViewing(null);
      setPurging(null);
      refresh();
      toast(RECEIVED_MSG.purgedToast);
    } catch (e) {
      setPurging(null);
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(false);
    }
  };

  const items = list.data?.items ?? [];
  const deletedCount = list.data?.deletedCount ?? 0;
  const unsorted = items.filter((d) => d.kind === "unsorted").length;
  const empty = items.length === 0 && pending.length === 0;
  const key = pasteKey();
  const chooseInput = (testId: string) => (
    <input
      type="file"
      className="vh"
      accept={RECEIVED_ACCEPT}
      multiple
      data-testid={testId}
      onChange={(e) => {
        const files = Array.from(e.target.files ?? []);
        e.target.value = "";
        if (files.length) addFiles(files, "upload");
      }}
    />
  );

  return (
    <section className="paper grecv" data-testid="group-received-box">
      <div className="row grecv-head">
        <h2 style={{ margin: 0 }}>
          {RECEIVED_MSG.title}
          <Help testId="help-received" text={t((docRead ? RECEIVED_READ_MSG.help : RECEIVED_MSG.help).replace("{paste}", key))} />
        </h2>
        {docRead && !small && quota?.limit != null ? (
          <span className="rdunsorted" data-testid="received-quota">
            {RECEIVED_READ_MSG.quota(quota.used, quota.limit)}
          </span>
        ) : null}
        <span className="sp" />
        {unsorted > 0 ? (
          <span className="rdunsorted" data-testid="received-unsorted-count">
            {small ? RECEIVED_MSG.unsortedCountShort(unsorted) : RECEIVED_MSG.unsortedCount(unsorted)}
          </span>
        ) : null}
        {/* PC版：右上に「ファイル選択」（入力欄の <label>）。スマホ版は点線の中の 56px のボタン */}
        {phone ? null : (
          <label className="btn small fdpick" data-testid="received-choose">
            {RECEIVED_MSG.choose}
            {chooseInput("received-file-input")}
          </label>
        )}
      </div>
      <FileDrop
        onFiles={addFiles}
        accept={RECEIVED_ACCEPT}
        phoneAccept={RECEIVED_PHONE_ACCEPT}
        hint={t(RECEIVED_MSG.dropHint.replace("{paste}", key))}
        compact={!empty}
        testId="received-drop"
        inputTestId="received-file-input"
        hideButton={!phone}
      />
      {/* #281：全部を削除したときも「削除済 n ›」で開いた削除済の行は表に出す（前は生きている行が無いと表ごと出さず、復元も完全に削除もできなかった） */}
      {pending.length || items.length || (showDeleted && deletedCount > 0) ? (
        <table className={"rdtable" + (docRead ? " has-read" : "") + (small ? " is-small" : "")} data-testid="received-table">
          <thead>
            <tr>
              <th className="rd-kind">{RECEIVED_MSG.col.kind}</th>
              <th className="rd-name">{RECEIVED_MSG.col.name}</th>
              <th className="rd-date">{RECEIVED_MSG.col.date}</th>
              <th className="rd-by">{RECEIVED_MSG.col.by}</th>
              <th className="rd-st">{RECEIVED_MSG.col.state}</th>
              <th className="rd-more" aria-label={RECEIVED_MSG.more} />
            </tr>
          </thead>
          <tbody>
            {pending.map((p) => (
              <tr key={p.key} className={"rdpending" + (p.state === "error" ? " is-error" : "")} data-testid="received-pending">
                <td className="rd-kind" />
                <td className="rd-name" colSpan={4}>
                  <span className="rdname-static" title={p.name}>{p.name}</span>
                  {p.state === "error" ? (
                    <span className="rderr" role="alert" data-testid="received-pending-error">
                      ⚠ {p.msg}
                      {!p.retry ? null : (
                        <button type="button" className="btn text" onClick={() => setPending((x) => x.map((y) => (y.key === p.key ? { ...y, state: "waiting", msg: undefined } : y)))} data-testid="received-retry">
                          {RECEIVED_MSG.retry}
                        </button>
                      )}
                      <button type="button" className="btn text" onClick={() => setPending((x) => x.filter((y) => y.key !== p.key))} data-testid="received-pending-cancel">
                        {RECEIVED_MSG.cancel}
                      </button>
                    </span>
                  ) : (
                    <span className="muted rdadding" data-testid="received-pending-state">
                      {p.state === "waiting" ? RECEIVED_MSG.waiting : RECEIVED_MSG.adding}
                    </span>
                  )}
                </td>
                <td className="rd-more" />
              </tr>
            ))}
            {items.map((d) => (
              <tr key={d.id} className={d.kind === "unsorted" ? "is-unsorted" : undefined} data-testid="received-row" data-id={d.id}>
                <td className="rd-kind">
                  {small ? (
                    <button
                      type="button"
                      className={"rdchip" + (d.kind === "unsorted" ? " is-unsorted" : "")}
                      aria-haspopup="dialog"
                      aria-label={`${d.title} の${RECEIVED_MSG.col.kind}：${d.kind === "unsorted" ? RECEIVED_MSG.pickKind : RECEIVED_KIND_LABEL[d.kind]}`}
                      onClick={() => setKindSheet({ doc: d, open: true })}
                      data-testid="received-kind-chip"
                    >
                      <span className="rdchip-t">{d.kind === "unsorted" ? RECEIVED_MSG.pickKind : RECEIVED_KIND_LABEL[d.kind]}</span>
                      <span className="rdchip-cv" aria-hidden="true" />
                    </button>
                  ) : (
                    <select
                      className={"inp rdkind" + (d.kind === "unsorted" ? " is-unsorted" : "")}
                      value={d.kind}
                      aria-label={`${d.title} の${RECEIVED_MSG.col.kind}`}
                      onChange={(e) => void changeKind(d, e.target.value as ReceivedKind)}
                      data-testid="received-kind"
                    >
                      {RECEIVED_KINDS.map((k) => (
                        <option key={k} value={k}>
                          {receivedKindOption(k)}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td className="rd-name">
                  <span className="rdnamecell">
                    <button type="button" className="rdname" title={d.title} onClick={() => setViewing(d)} data-testid="received-name">
                      {d.title}
                    </button>
                    {d.pageCount ? <span className="muted num rdpages">{RECEIVED_MSG.pages(d.pageCount)}</span> : null}
                  </span>
                  <UsedIn d={d} />
                </td>
                <td className="rd-date num">{fmtDateTimeShort(d.createdAt)}</td>
                <td className="rd-by">
                  <span className="rdby" title={d.uploadedBy ?? ""} data-testid="received-by">
                    {small ? familyNameOf(d.uploadedBy) : (d.uploadedBy ?? "—")}
                  </span>
                </td>
                <td className="rd-st">{stateCell(d)}</td>
                <td className="rd-more">
                  <RowMenu
                    label={`${d.title} の${RECEIVED_MSG.more}`}
                    testId="received-more"
                    items={[
                      { label: RECEIVED_MSG.rename, onSelect: () => setRenaming(d), testId: "received-rename" },
                      { label: RECEIVED_MSG.delete, onSelect: () => setDeleting(d), danger: true, testId: "received-delete" },
                    ]}
                  />
                </td>
              </tr>
            ))}
            {showDeleted
              ? (deleted.data?.items ?? []).map((d) => (
                  <tr key={d.id} className="is-deleted" data-testid="received-deleted-row">
                    <td className="rd-kind">{RECEIVED_KIND_LABEL[d.kind]}</td>
                    <td className="rd-name">
                      <button type="button" className="rdname" title={d.title} onClick={() => setViewing(d)}>
                        {d.title}
                      </button>
                    </td>
                    <td className="rd-date num">{fmtDateTimeShort(d.createdAt)}</td>
                    {/* #281：「削除済」「復元」の右に「完全に削除」（朱の文字）。3つ並ぶ幅を取るため 登録者・状態・操作の列をつなぐ（削除済の行は登録者を出さない） */}
                    <td className="rd-st rd-delacts" colSpan={3}>
                      <span className="rddelacts">
                        <span className="muted rddelst">{RECEIVED_MSG.deleted}</span>
                        <button type="button" className="btn small" onClick={() => void restore(d)} data-testid="received-restore">
                          {RECEIVED_MSG.restore}
                        </button>
                        <button type="button" className="btn text danger" onClick={() => setPurging(d)} aria-label={`${d.title} を${RECEIVED_MSG.purge}`} data-testid="received-purge">
                          {RECEIVED_MSG.purge}
                        </button>
                      </span>
                    </td>
                  </tr>
                ))
              : null}
          </tbody>
        </table>
      ) : null}
      {deletedCount > 0 ? (
        <div className="rdfoot">
          <button type="button" className="btn text" aria-expanded={showDeleted} onClick={() => setShowDeleted((v) => !v)} data-testid="received-deleted-toggle">
            {RECEIVED_MSG.deleted} {deletedCount} {showDeleted ? "‹" : "›"}
          </button>
        </div>
      ) : null}
      {list.isError ? <ErrorText>{messageOf(list.error)}</ErrorText> : null}

      <ReceivedViewer doc={viewing} onClose={closeViewer} onDelete={(d) => setDeleting(d)} onChanged={refresh} toast={toast} />
      {docRead && !small ? <ReceivedReadDrawer doc={reading} onClose={closeReading} onChanged={refresh} toast={toast} /> : null}
      {kindSheet ? (
        <KindSheet
          doc={kindSheet.doc}
          open={kindSheet.open}
          onClose={() => setKindSheet((x) => (x ? { ...x, open: false } : x))}
          onPick={(d, k) => {
            setKindSheet((x) => (x ? { ...x, open: false } : x));
            if (k !== d.kind) void changeKind(d, k);
          }}
        />
      ) : null}
      {deleting ? <DeleteDialog doc={deleting} busy={busy} onCancel={() => setDeleting(null)} onDelete={() => void remove()} /> : null}
      {purging ? <DeleteDialog doc={purging} busy={busy} purge onCancel={() => setPurging(null)} onDelete={() => void purge()} /> : null}
      {renaming ? (
        <RenameDialog
          doc={renaming}
          onCancel={() => setRenaming(null)}
          onDone={() => {
            setRenaming(null);
            refresh();
          }}
        />
      ) : null}
      <Sheet
        open={pasteText != null}
        title={RECEIVED_MSG.pasteSheetTitle}
        cancelLabel={RECEIVED_MSG.cancel}
        doneLabel={RECEIVED_MSG.pasteAdd}
        doneDisabled={pasteBusy || (pasteText?.length ?? 0) > RECEIVED_TEXT_MAX}
        onCancel={() => {
          setPasteText(null);
          setPasteErr(undefined);
        }}
        onDone={() => void addText()}
        testId="received-paste-sheet"
        doneTestId="received-paste-add"
        cancelTestId="received-paste-cancel"
      >
        <pre className="rdpaste" data-testid="received-paste-preview">
          {(pasteText ?? "").slice(0, 200)}
          {(pasteText ?? "").length > 200 ? "…" : ""}
        </pre>
        <p className="hint" style={{ margin: "6px 0 0" }}>{RECEIVED_MSG.pasteHint.replace("{n}", (pasteText ?? "").length.toLocaleString("ja-JP"))}</p>
        <ErrorText>{pasteErr}</ErrorText>
      </Sheet>
      {over
        ? createPortal(
            <div className="dropover" aria-hidden="true" data-testid="received-overlay">
              <span className="dropover-tag">{RECEIVED_MSG.overlay}</span>
            </div>,
            document.body,
          )
        : null}
      {node}
    </section>
  );
}

/** 登録者の姓（最初の空白まで。全角の空白も）。スマホの一覧は姓だけ（S4 ⑦） */
function familyNameOf(name: string | null): string {
  if (!name) return "—";
  return name.trim().split(/[\s\u3000]+/)[0] || name;
}

/** 書類を選ぶシート（スマホ、S4 ⑦・絵＝PhoneSheet）：題「書類」＋右に書類の名前、6つの行（今の書類は淡い藍＋✓）、下に「閉じる」 */
const SHEET_KINDS = RECEIVED_KINDS.filter((k): k is Exclude<ReceivedKind, "unsorted"> => k !== "unsorted");
function KindSheet({ doc: d, open, onClose, onPick }: { doc: ReceivedDocument; open: boolean; onClose: () => void; onPick: (d: ReceivedDocument, k: ReceivedKind) => void }) {
  return (
    <Sheet open={open} title={RECEIVED_MSG.kindSheetTitle} actions cancelLabel={RECEIVED_MSG.close} onCancel={onClose} className="rdkindsheet" testId="received-kind-sheet" cancelTestId="received-kind-sheet-close">
      <div className="rdks-head">
        <h3 className="rdks-ttl">{RECEIVED_MSG.kindSheetTitle}</h3>
        <span className="rdks-name">
          <Trunc key={d.id}>{d.title}</Trunc>
        </span>
      </div>
      {SHEET_KINDS.map((k) => {
        const on = d.kind === k;
        return (
          <button key={k} type="button" className={"rdks-opt" + (on ? " is-on" : "")} aria-pressed={on} onClick={() => onPick(d, k)} data-testid="received-kind-option" data-kind={k}>
            <KindIcon kind={k} />
            <span className="rdks-t">{RECEIVED_KIND_LABEL[k]}</span>
            {on ? <CheckIcon size={20} /> : null}
          </button>
        );
      })}
    </Sheet>
  );
}

/** 削除の窓（#138：題も「削除」）。「削除済」から復元できる。purge＝完全に削除の窓（#281：題「完全に削除」・原本も消え元に戻せない・主は「取消」） */
function DeleteDialog({ doc, busy, purge, onCancel, onDelete }: { doc: ReceivedDocument; busy: boolean; purge?: boolean; onCancel: () => void; onDelete: () => void }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onCancel();
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onCancel]);
  return (
    <div className="dim rddim" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="dlg" role="alertdialog" aria-label={purge ? RECEIVED_MSG.purgeTitle : RECEIVED_MSG.delete} data-testid={purge ? "received-purge-dialog" : "received-delete-dialog"}>
        <div className="dh">{purge ? RECEIVED_MSG.purgeTitle : RECEIVED_MSG.delete}</div>
        <div className="db">
          <p style={{ margin: 0 }}>{(purge ? RECEIVED_MSG.purgeBody : RECEIVED_MSG.deleteBody).replace("{title}", doc.title)}</p>
        </div>
        <div className="df">
          <button type="button" className="btn" onClick={onCancel} autoFocus>
            {RECEIVED_MSG.cancel}
          </button>
          <button type="button" className="btn shu" disabled={busy} onClick={onDelete} data-testid={purge ? "received-purge-confirm" : "received-delete-confirm"}>
            {RECEIVED_MSG.delete}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 名前変更（表示名だけ。原本のファイルは変わらない） */
function RenameDialog({ doc, onCancel, onDone }: { doc: ReceivedDocument; onCancel: () => void; onDone: () => void }) {
  const [name, setName] = useState(doc.title);
  const [err, setErr] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onCancel();
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onCancel]);
  const submit = async () => {
    if (!name.trim()) return setErr("名前を入れてください");
    setBusy(true);
    try {
      await api<ReceivedDocument>("PATCH", `/received-documents/${doc.id}`, { title: name.trim() });
      onDone();
    } catch (e) {
      setErr(messageOf(e));
      setBusy(false);
    }
  };
  return (
    <div className="dim rddim" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="dlg" role="dialog" aria-label={RECEIVED_MSG.renameTitle} data-testid="received-rename-dialog">
        <div className="dh">{RECEIVED_MSG.renameTitle}</div>
        <div className="db">
          <Field label={RECEIVED_MSG.col.name} id="rd-rename">
            <input
              id="rd-rename"
              className="inp"
              value={name}
              maxLength={RECEIVED_TITLE_MAX}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                guardComposingEnter(e);
                if (e.key === "Enter" && !e.defaultPrevented) void submit();
              }}
              data-testid="received-rename-input"
            />
          </Field>
          <ErrorText>{err}</ErrorText>
        </div>
        <div className="df">
          <button type="button" className="btn" onClick={onCancel}>
            {RECEIVED_MSG.cancel}
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={() => void submit()} data-testid="received-rename-save">
            {RECEIVED_MSG.renameSave}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 「依頼に使用 RQ-0003 沼田電気」（#218、docs/88 §4.6）：送った見積依頼に付けた書類の名前の下。2件以上は「依頼に使用 n件」（乗せると全部・押すと最初の依頼）。
 * 下書きの依頼は数えない（API の usedIn）
 */
function UsedIn({ d }: { d: ReceivedDocument }) {
  const u = d.usedIn;
  if (!u.length) return null;
  const all = u.map((x) => `${x.no} ${x.vendor}`).join("\n");
  const text = u.length === 1 ? RECEIVED_MSG.usedIn.replace("{no}", u[0]!.no).replace("{vendor}", u[0]!.vendor) : RECEIVED_MSG.usedInMany.replace("{n}", String(u.length));
  return (
    <Link className="rd-used" to={`/rfqs/${u[0]!.id}`} title={all} data-testid="received-used-in">
      {text}
    </Link>
  );
}

